import React from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import SortableItem from "./SortableItem.tsx";
import {
  Banner,
  BannerStatus,
  useUpdateBanners,
} from "@/services/banner.service.ts";
import { Button } from "@nextui-org/react";
import { BackArrowIcon } from "@/assets/icons/admin";
import { toast } from "react-toastify";

interface ReorderBannerProps {
  banners: Banner[];
  setBanners: React.Dispatch<React.SetStateAction<Banner[]>>;
  onCancel: () => void;
}

const ReorderBanner: React.FC<ReorderBannerProps> = ({
  banners,
  setBanners,
  onCancel,
}) => {
  const editBanners = useUpdateBanners();

  // Filter banners that are only in SHOWING status
  const showingBanners = banners.filter(
    (banner) => banner.status === BannerStatus.SHOWING,
  );

  const handleDragEnd = (event: { active: any; over: any }) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = showingBanners.findIndex(
        (item) => item.id === active.id,
      );
      const newIndex = showingBanners.findIndex((item) => item.id === over.id);
      setBanners(arrayMove(showingBanners, oldIndex, newIndex));
    }
  };

  const handleSaveChanges = () => {
    const payload = showingBanners.map((banner, index) => ({
      id: banner.id,
      orderNumber: index + 1, // Order starts from 1
    }));

    editBanners.mutate(payload, {
      onSuccess: () => {
        console.log("Banners reordered successfully!");
        toast.success("Banner reordered successfully");
      },
      onError: (error) => {
        console.error("Failed to reorder banners:", error);
      },
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-2 flex items-center">
        <Button isIconOnly onPress={onCancel} className="text-white">
          <BackArrowIcon />
        </Button>
        <h2 className="text-xl font-semibold text-white">Reorder Banner</h2>
      </div>
      <p className="mb-2 text-gray-400">
        Grab and move the dots icon to reorder the banners. The top banner will
        show as the most left banner and so on.
      </p>

      {/* Drag and Drop Context */}
      {showingBanners.length > 0 ? (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={showingBanners.map((item) => item.id)}>
            {showingBanners.map((banner) => (
              <SortableItem key={banner.id} banner={banner} />
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        /* Show empty state when no banners */
        <div className="flex h-48 w-full items-center justify-center bg-[#12122e] text-xl text-gray-400">
          no banner available for reordering
        </div>
      )}

      {/* Save Button */}
      <div className="mt-8 flex justify-end space-x-4">
        <button
          onClick={handleSaveChanges}
          className="rounded-md bg-gradient-to-r from-[#2E65FF] to-[#003BDA] px-6 py-2 text-white transition-all hover:from-[#1F4ADF] hover:to-[#002DBB]"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default ReorderBanner;
